import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { showSaveSuccess, showSaveError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { fixedAssetService, FixedAsset } from '@/services/fixedAssetService'
import { accountManagementService } from '@/services/accountManagementService'
import type { Account } from '@/api/types'

// Validation schema
const assetDisposalSchema = z.object({
  fixedAssetId: z.number().min(1, 'Asset is required'),
  fixedAssetAccount: z.number().min(1, 'Fixed Asset Account is required'),
  collectionAccount: z.number().min(1, 'Collection Account is required'),
  gainLossAccount: z.number().min(1, 'Gain/Loss Account is required'),
  saleAmount: z.number().min(0, 'Sale amount must be 0 or greater'),
  saleDate: z.string().min(1, 'Sale date is required'),
})

type AssetDisposalFormData = z.infer<typeof assetDisposalSchema>

/**
 * Asset Disposal Detail Page
 * Form for creating new asset disposals
 */
export const AssetDisposalsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [assets, setAssets] = useState<FixedAsset[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [selectedAsset, setSelectedAsset] = useState<FixedAsset | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<AssetDisposalFormData>({
    resolver: zodResolver(assetDisposalSchema),
    defaultValues: {
      fixedAssetId: 0,
      fixedAssetAccount: 0,
      collectionAccount: 0,
      gainLossAccount: 0,
      saleAmount: 0,
      saleDate: '',
    },
  })

  const fixedAssetId = watch('fixedAssetId')

  // Load assets and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [assetsResponse, accountsData] = await Promise.all([
          fixedAssetService.getAllAssets(branchId, 1, 1000),
          accountManagementService.getAllAccounts(branchId),
        ])

        setAssets(assetsResponse.data || [])
        setAccounts(accountsData || [])
      } catch (error) {
        console.error('Error loading lookups:', error)
        showSaveError(error as Error, false)
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Auto-fill form when asset is selected
  useEffect(() => {
    if (fixedAssetId && fixedAssetId > 0) {
      const asset = assets.find(a => a.id === fixedAssetId)
      if (asset) {
        setSelectedAsset(asset)
        
        // Auto-fill all fields from selected asset
        if (asset.fixedAssetAccountId) {
          setValue('fixedAssetAccount', asset.fixedAssetAccountId)
        }
        
        // Set default sale date to today
        const today = new Date().toISOString().split('T')[0]
        setValue('saleDate', today)
      }
    } else {
      setSelectedAsset(null)
    }
  }, [fixedAssetId, assets, setValue])

  // Format date for input field
  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      return date.toISOString().split('T')[0]
    } catch {
      return dateString.split('T')[0] || ''
    }
  }

  // Format account for display
  const formatAccount = (account: Account): string => {
    const code = account.accountCode || ''
    const name = account.accountName || account.accountNameEn || account.accountNameAr || ''
    return code ? `${code} (${name})` : name
  }

  // Asset options for dropdown
  const assetOptions = assets.map(asset => ({
    value: String(asset.id || 0),
    label: `${asset.fixedAssetNumber || ''} - ${asset.name || ''}`.trim() || `Asset #${asset.id}`,
  }))

  // Account options for dropdowns
  const accountOptions = accounts.map(account => ({
    value: String(account.accountId || account.id || 0),
    label: formatAccount(account),
  }))

  const onSubmit = async (data: AssetDisposalFormData) => {
    if (!branchId) {
      await showSaveError(new Error(t('assetDisposals.messages.branchNotSelected')), false)
      return
    }

    try {
      setLoading(true)

      // Convert sale date to ISO format
      const saleDateISO = data.saleDate 
        ? new Date(`${data.saleDate}T00:00:00`).toISOString()
        : new Date().toISOString()

      // Prepare disposal data for API
      const disposalData = {
        fixedAssetId: data.fixedAssetId,
        fixedAssetAccount: data.fixedAssetAccount,
        collectionAccount: data.collectionAccount,
        gainLossAccount: data.gainLossAccount,
        saleAmount: data.saleAmount,
        saleDate: saleDateISO,
      }

      const response = await fixedAssetService.saveDisposal(disposalData)

      if (response && response.success !== false) {
        await showSaveSuccess(false, t('assetDisposals.messages.disposalSavedSuccess'))
        navigate('/asset-disposals')
      } else {
        await showSaveError(
          new Error(response?.message || t('assetDisposals.messages.failedToSaveDisposal')),
          false
        )
      }
    } catch (error: any) {
      console.error('Error saving asset disposal:', error)
      await showSaveError(
        error.response?.data?.message || error.message || t('assetDisposals.messages.failedToSaveDisposal'),
        false
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('assetDisposals.title')}
        description={t('assetDisposals.subtitle')}
        action={{
          label: t('assetDisposals.return'),
          onClick: () => navigate('/asset-disposals'),
          icon: ArrowLeft,
        }}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Asset Selection */}
        <Card>
          <CardHeader>
            <CardTitle>{t('assetDisposals.sections.asset')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>{t('assetDisposals.fields.asset')}</Label>
              <SearchableSelect
                options={assetOptions}
                value={fixedAssetId ? String(fixedAssetId) : '0'}
                onValueChange={(value) => setValue('fixedAssetId', Number(value) || 0)}
                placeholder={t('assetDisposals.placeholders.selectAsset')}
                searchPlaceholder={t('assetDisposals.placeholders.searchAssets')}
                emptyMessage={t('assetDisposals.placeholders.noAssetsFound')}
              />
              {errors.fixedAssetId && (
                <p className="text-sm text-destructive">{errors.fixedAssetId.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Fixed Asset Info - Read Only */}
        {selectedAsset && (
          <Card>
            <CardHeader>
              <CardTitle>{t('assetDisposals.sections.fixedAssetInfo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.name')}</Label>
                  <Input value={selectedAsset.name || ''} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.fixedAssetNumber')}</Label>
                  <Input value={selectedAsset.fixedAssetNumber || ''} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.assetCategory')}</Label>
                  <Input value={selectedAsset.assetCategoryName || ''} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.purchaseDate')}</Label>
                  <Input 
                    type="date"
                    value={formatDateForInput(selectedAsset.purchaseDate)} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.purchasePrice')}</Label>
                  <Input 
                    type="number"
                    value={selectedAsset.purchasePrice || 0} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.warrantyExpiry')}</Label>
                  <Input 
                    type="date"
                    value={formatDateForInput(selectedAsset.warrantyExpiry)} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.serialNumber')}</Label>
                  <Input value={selectedAsset.serialNumber || ''} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.location')}</Label>
                  <Input value={selectedAsset.costLocationId || ''} readOnly className="bg-muted" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Description - Read Only */}
        {selectedAsset && selectedAsset.description && (
          <Card>
            <CardHeader>
              <CardTitle>{t('assetDisposals.sections.description')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea 
                value={selectedAsset.description || ''} 
                readOnly 
                className="bg-muted min-h-[100px]" 
              />
            </CardContent>
          </Card>
        )}

        {/* Book Value - Read Only */}
        {selectedAsset && (
          <Card>
            <CardHeader>
              <CardTitle>{t('assetDisposals.sections.bookValue')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.depreciationStartDate')}</Label>
                  <Input 
                    type="date"
                    value={formatDateForInput(selectedAsset.depreciationStartDate)} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.fixedAssetAccount')}</Label>
                  <Input 
                    value={
                      selectedAsset.fixedAssetAccountId
                        ? formatAccount(accounts.find(a => (a.accountId || a.id) === selectedAsset.fixedAssetAccountId) || {} as Account)
                        : ''
                    }
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.depreciationAccount')}</Label>
                  <Input 
                    value={
                      selectedAsset.depreciationAccountId
                        ? formatAccount(accounts.find(a => (a.accountId || a.id) === selectedAsset.depreciationAccountId) || {} as Account)
                        : ''
                    }
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.monthlyDepreciation')}</Label>
                  <Input 
                    type="number"
                    value={selectedAsset.monthlyDepreciation || 0} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.bookValue')}</Label>
                  <Input 
                    type="number"
                    value={selectedAsset.bookValue || 0} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.depreciationMethod')}</Label>
                  <Input value={selectedAsset.depreciationMethod || ''} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.collectionAccount')}</Label>
                  <SearchableSelect
                    options={accountOptions}
                    value={watch('collectionAccount') ? String(watch('collectionAccount')) : '0'}
                    onValueChange={(value) => setValue('collectionAccount', Number(value) || 0)}
                    placeholder={t('assetDisposals.placeholders.selectCollectionAccount')}
                    searchPlaceholder={t('assetDisposals.placeholders.searchAccounts')}
                    emptyMessage={t('assetDisposals.placeholders.noAccountsFound')}
                  />
                  {errors.collectionAccount && (
                    <p className="text-sm text-destructive">{errors.collectionAccount.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.expenseAccount')}</Label>
                  <Input 
                    value={
                      selectedAsset.expenseAccountId
                        ? formatAccount(accounts.find(a => (a.accountId || a.id) === selectedAsset.expenseAccountId) || {} as Account)
                        : ''
                    }
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.accumulatedDepreciation')}</Label>
                  <Input 
                    type="number"
                    value={selectedAsset.accumulatedDepreciation || 0} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.saleAmount')}</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    {...register('saleAmount', { valueAsNumber: true })}
                    placeholder={t('assetDisposals.placeholders.saleAmount')}
                  />
                  {errors.saleAmount && (
                    <p className="text-sm text-destructive">{errors.saleAmount.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.effectiveLifeYears')}</Label>
                  <Input 
                    type="number"
                    value={selectedAsset.effectiveLifeYears || 0} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.gainLossAccount')}</Label>
                  <SearchableSelect
                    options={accountOptions}
                    value={watch('gainLossAccount') ? String(watch('gainLossAccount')) : '0'}
                    onValueChange={(value) => setValue('gainLossAccount', Number(value) || 0)}
                    placeholder={t('assetDisposals.placeholders.selectGainLossAccount')}
                    searchPlaceholder={t('assetDisposals.placeholders.searchAccounts')}
                    emptyMessage={t('assetDisposals.placeholders.noAccountsFound')}
                  />
                  {errors.gainLossAccount && (
                    <p className="text-sm text-destructive">{errors.gainLossAccount.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.yearlyRate')}</Label>
                  <Input 
                    type="number"
                    value={selectedAsset.yearlyRate || 0} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.notDepreciableValue')}</Label>
                  <Input 
                    type="number"
                    value={selectedAsset.notDepreciableValue || 0} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('assetDisposals.fields.saleDate')}</Label>
                  <Input 
                    type="date"
                    {...register('saleDate')}
                  />
                  {errors.saleDate && (
                    <p className="text-sm text-destructive">{errors.saleDate.message}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/asset-disposals')}
            disabled={loading}
          >
            {t('assetDisposals.cancel')}
          </Button>
          <Button type="submit" disabled={loading || !selectedAsset}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? t('assetDisposals.saving') : t('assetDisposals.save')}
          </Button>
        </div>
      </form>
    </div>
  )
}

